import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import {
  Button,
  CalendarInput,
  SelectComponent,
  Textarea,
  Checkbox,
} from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { InvestmentPolicyValues } from "./types";
import { investmentPolicySchema } from "./validationSchemas";
import {
  DURATION_OPTIONS,
  PREMIUM_PAYMENT_OPTIONS,
  RELIANCE_CONSENT_LABEL,
} from "./constants";

interface InvestmentPolicyFormProps {
  item: InvestmentItem;
  onSubmit: (values: InvestmentPolicyValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
  isAdmin?: boolean;
}

const InvestmentPolicyForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
  isAdmin = false,
}: InvestmentPolicyFormProps) => {
  const contractDate = item.details?.contractDate;
  const durationDateRaw = item.details?.durationDate;
  const premiumPayment = item.details?.premiumPayment;

  let durationDate = "";
  let durationDateValue = "";

  if (durationDateRaw === null || durationDateRaw === undefined) {
    durationDate = "indefinite";
  } else if (typeof durationDateRaw === "string" && durationDateRaw !== "") {
    const d = new Date(durationDateRaw);
    if (
      !isNaN(d.getTime()) &&
      d.getFullYear() === 2000 &&
      d.getMonth() === 0 &&
      d.getDate() === 1
    ) {
      durationDate = "";
    } else if (!isNaN(d.getTime())) {
      durationDate = "specific_date";
      durationDateValue = durationDateRaw;
    }
  }

  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const initialValues: InvestmentPolicyValues = {
    name: item.name ?? "",
    currencyUuid: item.currencyUuid ?? "",
    contractDate: contractDate ? String(contractDate) : "",
    durationDate,
    durationDateValue,
    premiumPayment: premiumPayment ? String(premiumPayment) : "",
    additionalInfo: item.additionalInfo ?? "",
    relianceCanView: item.relianceCanView ?? false,
    contractFile: null,
    status: item.status ?? "active",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={investmentPolicySchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => {
        console.log(formik.values.durationDateValue);
        return (
          <Form>
            <ScrollToFirstError />
            <div className="modal-form__fields">
              <Field
                as={Input}
                name="name"
                label="Nazwa"
                type="text"
                error={
                  (formik.touched.name || formik.submitCount > 0) &&
                  formik.errors.name
                    ? formik.errors.name
                    : undefined
                }
              />
              <Field
                as={SelectComponent}
                name="currencyUuid"
                label="Waluta"
                options={currencyOptions}
                isSearchable={false}
                error={
                  (formik.touched.currencyUuid || formik.submitCount > 0) &&
                  formik.errors.currencyUuid
                    ? formik.errors.currencyUuid
                    : undefined
                }
              />
              <Field
                type="date"
                name="contractDate"
                label="Data zawarcia"
                optional
                component={CalendarInput}
                errors={formik.errors}
                yearMonthPicker={true}
                maxDate={
                  formik.values.durationDateValue
                    ? (() => {
                        const d = new Date(formik.values.durationDateValue);
                        d.setDate(d.getDate() - 1);
                        return d;
                      })()
                    : undefined
                }
              />
              <Field
                as={SelectComponent}
                name="durationDate"
                label="Czas trwania"
                options={DURATION_OPTIONS}
                isSearchable={false}
                optional
              />
              {formik.values.durationDate === "specific_date" && (
                <Field
                  type="date"
                  name="durationDateValue"
                  label="Data zakończenia"
                  component={CalendarInput}
                  errors={formik.errors}
                  yearMonthPicker={true}
                  minDate={
                    formik.values.contractDate
                      ? (() => {
                          const d = new Date(formik.values.contractDate);
                          d.setDate(d.getDate() + 1);
                          return d;
                        })()
                      : undefined
                  }
                />
              )}
              <Field
                as={SelectComponent}
                name="premiumPayment"
                label="Składki"
                options={PREMIUM_PAYMENT_OPTIONS}
                isSearchable={false}
                optional
              />
              <Field
                component={Textarea}
                name="additionalInfo"
                label="Dodatkowe informacje"
                optional
              />
              <Field
                label="Umowa"
                name="contractFile"
                id="contractFile"
                as={DropFileInput}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                optional
                multiple={10}
                maxSize={5}
                description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, JPEG, PNG)"
                existingFiles={existingContractFiles.map((f: any) => ({
                  name: f.file?.name || "Plik",
                  url: f.file?.url || "",
                }))}
                onServerFileRemoved={onRemovedContract}
              />
              {!isAdmin && (
                <Field
                  as={Checkbox}
                  name="relianceCanView"
                  label={RELIANCE_CONSENT_LABEL}
                  checked={formik.values.relianceCanView}
                  onChange={() =>
                    formik.setFieldValue(
                      "relianceCanView",
                      !formik.values.relianceCanView,
                    )
                  }
                />
              )}
            </div>

            <div className="modal-buttons">
              <Button
                type="button"
                className="button--stroke button--rounded"
                onClick={onCancel}
                label="Anuluj"
              />
              <Button
                type="submit"
                className="button--black button--rounded"
                onClick={() => {}}
                label={isPending ? "Zapisywanie..." : "Zapisz"}
                isLoading={isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default InvestmentPolicyForm;
